import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { Card } from "@/components/ui/Card";
import { Spinner } from "@/components/ui/Spinner";
import { fmtDateTime } from "@/pages/anfragen/anfrageWizardUtils";
import {
  getInvoiceMailLieferantUebersicht,
  lieferantEinvoiceStatusClass,
  lieferantEinvoiceStatusLabel,
  type InvoiceMailLieferantUebersichtItem,
  type InvoiceMailLieferantUebersichtResponse,
} from "@/lib/invoiceMail";

function senderDisplay(row: InvoiceMailLieferantUebersichtItem): string {
  const name = (row.lieferant_firmenname || row.from_name || "").trim();
  const email = (row.sender_email || "").trim();
  if (name && email) return `${name} · ${email}`;
  return name || email || "—";
}

export function InvoiceMailLieferantOverview() {
  const [data, setData] = useState<InvoiceMailLieferantUebersichtResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [expanded, setExpanded] = useState(true);
  const [showAll, setShowAll] = useState(false);

  useEffect(() => {
    setLoading(true);
    void getInvoiceMailLieferantUebersicht()
      .then(setData)
      .catch(() => setData(null))
      .finally(() => setLoading(false));
  }, []);

  const followupItems = useMemo(
    () => (data?.items || []).filter((i) => i.einvoice_status !== "always"),
    [data],
  );

  const visibleItems = showAll ? data?.items || [] : followupItems;

  if (loading) {
    return (
      <Card className="flex items-center gap-2 px-4 py-6 text-sm text-label-secondary">
        <Spinner className="h-4 w-4" />
        Lieferanten-Übersicht wird geladen…
      </Card>
    );
  }

  if (!data) return null;

  const { summary } = data;

  return (
    <Card className="overflow-hidden">
      <button
        type="button"
        className="flex w-full flex-wrap items-center justify-between gap-3 border-b border-separator px-4 py-3 text-left hover:bg-fill-quaternary/20"
        onClick={() => setExpanded((v) => !v)}
      >
        <div>
          <div className="font-medium text-label-primary">E-Rechnung nach Lieferant</div>
          <div className="text-xs text-label-secondary">
            {summary.needs_followup} Lieferant{summary.needs_followup === 1 ? "" : "en"} brauchen
            Follow-up · {summary.always_einvoice} senden durchgängig E-Rechnungen
          </div>
        </div>
        <div className="flex flex-wrap gap-2 text-xs">
          <span className="rounded-full bg-status-green/15 px-2 py-0.5 font-semibold text-status-green">
            {summary.always_einvoice} E-Rechnung
          </span>
          <span className="rounded-full bg-status-orange/15 px-2 py-0.5 font-semibold text-status-orange">
            {summary.partial_einvoice} teilweise
          </span>
          <span className="rounded-full bg-status-red/15 px-2 py-0.5 font-semibold text-status-red">
            {summary.no_einvoice} ohne
          </span>
        </div>
      </button>

      {expanded ? (
        <div className="px-4 py-3">
          <div className="mb-3 flex flex-wrap items-center justify-between gap-2">
            <p className="text-xs text-label-secondary">
              Lieferanten mit Rechnungs-Mails im Postfach ({summary.total_senders} gesamt)
            </p>
            <button
              type="button"
              className="text-xs font-medium text-accent hover:underline"
              onClick={() => setShowAll((v) => !v)}
            >
              {showAll ? "Nur Follow-up anzeigen" : "Alle Lieferanten anzeigen"}
            </button>
          </div>

          {visibleItems.length === 0 ? (
            <p className="py-4 text-center text-sm text-label-secondary">
              {showAll
                ? "Noch keine Lieferanten mit Rechnungs-Mails."
                : "Alle bekannten Lieferanten senden E-Rechnungen."}
            </p>
          ) : (
            <div className="max-h-72 overflow-auto rounded border border-separator">
              <table className="w-full min-w-[640px] text-left text-xs">
                <thead className="sticky top-0 bg-card text-label-secondary">
                  <tr>
                    <th className="px-3 py-2 font-medium">Lieferant</th>
                    <th className="px-3 py-2 font-medium">Status</th>
                    <th className="px-3 py-2 font-medium text-right">Mails</th>
                    <th className="px-3 py-2 font-medium text-right">E-Rechnung</th>
                    <th className="px-3 py-2 font-medium">Letzte Mail</th>
                  </tr>
                </thead>
                <tbody>
                  {visibleItems.map((row) => (
                    <tr key={row.sender_email} className="border-t border-separator/60">
                      <td className="px-3 py-2 align-top">
                        <div className="font-medium text-label-primary">{senderDisplay(row)}</div>
                        {row.lieferant_gp_id ? (
                          <Link
                            to={`/kontakte/geschaeftspartner/${row.lieferant_gp_id}`}
                            className="text-accent hover:underline"
                          >
                            Geschäftspartner öffnen
                          </Link>
                        ) : null}
                      </td>
                      <td className="px-3 py-2 align-top">
                        <span
                          className={`rounded-full px-2 py-0.5 text-xs font-semibold ${lieferantEinvoiceStatusClass(row.einvoice_status)}`}
                        >
                          {lieferantEinvoiceStatusLabel(row.einvoice_status)}
                        </span>
                      </td>
                      <td className="px-3 py-2 text-right align-top tabular-nums">
                        {row.mail_count}
                        {row.pending_count > 0 ? (
                          <span className="ml-1 text-status-orange">({row.pending_count} offen)</span>
                        ) : null}
                      </td>
                      <td className="px-3 py-2 text-right align-top tabular-nums">
                        {row.einvoice_mail_count}/{row.mail_count}
                      </td>
                      <td className="px-3 py-2 align-top whitespace-nowrap text-label-secondary">
                        {row.last_received_at ? fmtDateTime(row.last_received_at) : "—"}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      ) : null}
    </Card>
  );
}
